import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type {
  DocumentCategory,
  DocumentLinkedEntityType,
  DocumentStatus,
  GetDocumentResponse,
  ListDocumentsFilters,
  PropertyDocument,
} from '@/domain/entities/property-document';
import { logoutUser } from '@/features/auth/authThunks';
import type { RootState } from '@/store/root-state';
import {
  archivePropertyDocument,
  createAndUploadLinkedPropertyDocument,
  createAndUploadPropertyDocument,
  deletePropertyDocument,
  fetchPropertyDocument,
  fetchPropertyDocumentDownloadUrl,
  fetchPropertyDocumentTaxonomy,
  listEntityPropertyDocuments,
  listPropertyDocuments,
  replacePropertyDocumentFile,
  updatePropertyDocumentMetadata,
} from './propertyDocumentsThunks';
import type { PropertyDocumentBucketState, PropertyDocumentsState } from './propertyDocumentsTypes';

const ALL_CATEGORIES_BUCKET = 'ALL_CATEGORIES';
const ALL_SUBCATEGORIES_BUCKET = 'ALL_SUBCATEGORIES';
const ALL_STATUSES_BUCKET = 'ALL_STATUSES';

const EMPTY_DOCUMENTS: PropertyDocument[] = [];

const initialState: PropertyDocumentsState = {
  taxonomyByPropertyID: {},
  documentsByID: {},
  detailsByDocumentID: {},
  bucketsByKey: {},
  entityBucketsByKey: {},
  deletingByDocumentID: {},
  deletedByDocumentID: {},
  activeDownloadUrl: undefined,
  loading: false,
  uploading: false,
  error: undefined,
};

export const buildPropertyDocumentBucketKey = ({
  propertyID,
  filters = {},
}: {
  propertyID: string;
  filters?: ListDocumentsFilters;
}): string =>
  [
    propertyID,
    filters.category ?? ALL_CATEGORIES_BUCKET,
    filters.subcategory ?? ALL_SUBCATEGORIES_BUCKET,
    filters.status ?? ALL_STATUSES_BUCKET,
  ].join('|');

export const buildPropertyDocumentEntityBucketKey = ({
  entityType,
  entityID,
}: {
  entityType: DocumentLinkedEntityType;
  entityID: string;
}): string => [entityType, entityID].join('|');

function createBucketState(): PropertyDocumentBucketState {
  return {
    documentIDs: [],
    loading: false,
    loaded: false,
    error: undefined,
    lastFetchedAt: undefined,
  };
}

function ensureBucketState(
  state: Pick<PropertyDocumentsState, 'bucketsByKey'>,
  propertyID: string,
  filters?: ListDocumentsFilters,
): PropertyDocumentBucketState {
  const key = buildPropertyDocumentBucketKey({ propertyID, filters });
  state.bucketsByKey[key] ??= createBucketState();
  return state.bucketsByKey[key];
}

function ensureEntityBucketState(
  state: Pick<PropertyDocumentsState, 'entityBucketsByKey'>,
  payload: { entityType: DocumentLinkedEntityType; entityID: string },
): PropertyDocumentBucketState {
  const key = buildPropertyDocumentEntityBucketKey(payload);
  state.entityBucketsByKey[key] ??= createBucketState();
  return state.entityBucketsByKey[key];
}

function filtersFromBucketKey(bucketKey: string): { propertyID: string; filters: ListDocumentsFilters } | null {
  const [propertyID, category, subcategory, status] = bucketKey.split('|');
  if (!propertyID) return null;

  return {
    propertyID,
    filters: {
      category: category && category !== ALL_CATEGORIES_BUCKET ? (category as DocumentCategory) : undefined,
      subcategory: subcategory && subcategory !== ALL_SUBCATEGORIES_BUCKET ? subcategory : undefined,
      status: status && status !== ALL_STATUSES_BUCKET ? (status as DocumentStatus) : undefined,
    },
  };
}

function documentMatchesBucket(document: PropertyDocument, propertyID: string, filters: ListDocumentsFilters): boolean {
  if (document.propertyID !== propertyID) return false;
  if (filters.category && document.category !== filters.category) return false;
  if (filters.subcategory && document.subcategory !== filters.subcategory) return false;
  if (filters.status && document.status !== filters.status) return false;
  return true;
}

function upsertDocument(state: PropertyDocumentsState, document: PropertyDocument): void {
  state.documentsByID[document.documentID] = document;

  const detail = state.detailsByDocumentID[document.documentID];
  if (detail) {
    detail.document = document;
  }
}

function syncDocumentBucketsForUpsert(
  state: PropertyDocumentsState,
  previousDocument: PropertyDocument | undefined,
  nextDocument: PropertyDocument,
): void {
  Object.entries(state.bucketsByKey).forEach(([bucketKey, bucket]) => {
    if (!bucket) return;

    const bucketFilters = filtersFromBucketKey(bucketKey);
    if (!bucketFilters) return;

    const matchedBefore = previousDocument
      ? documentMatchesBucket(previousDocument, bucketFilters.propertyID, bucketFilters.filters)
      : false;
    const matchesNow = documentMatchesBucket(nextDocument, bucketFilters.propertyID, bucketFilters.filters);

    if (matchedBefore && !matchesNow) {
      bucket.documentIDs = bucket.documentIDs.filter((id) => id !== nextDocument.documentID);
      return;
    }

    if (matchesNow && !bucket.documentIDs.includes(nextDocument.documentID)) {
      bucket.documentIDs = [nextDocument.documentID, ...bucket.documentIDs];
    }
  });
}

function removeDocumentFromBuckets(state: PropertyDocumentsState, documentID: string): void {
  Object.values(state.bucketsByKey).forEach((bucket) => {
    if (bucket?.documentIDs.includes(documentID)) {
      bucket.documentIDs = bucket.documentIDs.filter((id) => id !== documentID);
    }
  });

  Object.values(state.entityBucketsByKey).forEach((bucket) => {
    if (bucket?.documentIDs.includes(documentID)) {
      bucket.documentIDs = bucket.documentIDs.filter((id) => id !== documentID);
    }
  });
}

function readError(action: { payload?: { message?: string }; error?: { message?: string } }, fallback: string): string {
  return action.payload?.message || action.error?.message || fallback;
}

const propertyDocumentsSlice = createSlice({
  name: 'propertyDocuments',
  initialState,
  reducers: {
    clearPropertyDocumentsError: (state) => {
      state.error = undefined;
    },
    clearPropertyDocumentDownloadUrl: (state) => {
      state.activeDownloadUrl = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPropertyDocumentTaxonomy.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchPropertyDocumentTaxonomy.fulfilled, (state, action) => {
        state.taxonomyByPropertyID[action.meta.arg.propertyID] = action.payload;
        state.loading = false;
      })
      .addCase(fetchPropertyDocumentTaxonomy.rejected, (state, action) => {
        state.loading = false;
        state.error = readError(action, 'Failed to fetch document taxonomy');
      })
      .addCase(listPropertyDocuments.pending, (state, action) => {
        const bucket = ensureBucketState(state, action.meta.arg.propertyID, action.meta.arg.filters);
        bucket.loading = true;
        bucket.error = undefined;
        state.error = undefined;
      })
      .addCase(listPropertyDocuments.fulfilled, (state, action) => {
        const bucket = ensureBucketState(state, action.meta.arg.propertyID, action.meta.arg.filters);
        action.payload.forEach((document) => upsertDocument(state, document));
        bucket.documentIDs = action.payload.map((document) => document.documentID);
        bucket.loading = false;
        bucket.loaded = true;
        bucket.error = undefined;
        bucket.lastFetchedAt = new Date().toISOString();
      })
      .addCase(listPropertyDocuments.rejected, (state, action) => {
        const bucket = ensureBucketState(state, action.meta.arg.propertyID, action.meta.arg.filters);
        bucket.loading = false;
        bucket.error = readError(action, 'Failed to list property documents');
        state.error = bucket.error;
      })
      .addCase(listEntityPropertyDocuments.pending, (state, action) => {
        const bucket = ensureEntityBucketState(state, action.meta.arg);
        bucket.loading = true;
        bucket.error = undefined;
        state.error = undefined;
      })
      .addCase(listEntityPropertyDocuments.fulfilled, (state, action) => {
        const bucket = ensureEntityBucketState(state, action.meta.arg);
        action.payload.forEach((document) => upsertDocument(state, document));
        bucket.documentIDs = action.payload.map((document) => document.documentID);
        bucket.loading = false;
        bucket.loaded = true;
        bucket.error = undefined;
        bucket.lastFetchedAt = new Date().toISOString();
      })
      .addCase(listEntityPropertyDocuments.rejected, (state, action) => {
        const bucket = ensureEntityBucketState(state, action.meta.arg);
        bucket.loading = false;
        bucket.error = readError(action, 'Failed to list entity documents');
        state.error = bucket.error;
      })
      .addCase(fetchPropertyDocument.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchPropertyDocument.fulfilled, (state, action: PayloadAction<GetDocumentResponse>) => {
        state.loading = false;
        state.detailsByDocumentID[action.payload.document.documentID] = action.payload;
        delete state.deletedByDocumentID[action.payload.document.documentID];
        upsertDocument(state, action.payload.document);
      })
      .addCase(fetchPropertyDocument.rejected, (state, action) => {
        state.loading = false;
        state.error = readError(action, 'Failed to fetch property document');
      })
      .addCase(createAndUploadPropertyDocument.pending, (state) => {
        state.uploading = true;
        state.error = undefined;
      })
      .addCase(createAndUploadPropertyDocument.fulfilled, (state, action) => {
        const previousDocument = state.documentsByID[action.payload.document.documentID];
        syncDocumentBucketsForUpsert(state, previousDocument, action.payload.document);
        upsertDocument(state, action.payload.document);
        state.uploading = false;
      })
      .addCase(createAndUploadPropertyDocument.rejected, (state, action) => {
        state.uploading = false;
        state.error = readError(action, 'Failed to upload property document');
      })
      .addCase(createAndUploadLinkedPropertyDocument.pending, (state) => {
        state.uploading = true;
        state.error = undefined;
      })
      .addCase(createAndUploadLinkedPropertyDocument.fulfilled, (state, action) => {
        const previousDocument = state.documentsByID[action.payload.document.documentID];
        syncDocumentBucketsForUpsert(state, previousDocument, action.payload.document);
        upsertDocument(state, action.payload.document);

        const bucket = ensureEntityBucketState(state, {
          entityType: action.payload.link.entityType,
          entityID: action.payload.link.entityID,
        });
        if (!bucket.documentIDs.includes(action.payload.document.documentID)) {
          bucket.documentIDs = [action.payload.document.documentID, ...bucket.documentIDs];
        }
        bucket.loaded = true;
        bucket.loading = false;
        bucket.error = undefined;
        bucket.lastFetchedAt = new Date().toISOString();
        state.uploading = false;
      })
      .addCase(createAndUploadLinkedPropertyDocument.rejected, (state, action) => {
        state.uploading = false;
        state.error = readError(action, 'Failed to upload linked property document');
      })
      .addCase(replacePropertyDocumentFile.pending, (state) => {
        state.uploading = true;
        state.error = undefined;
      })
      .addCase(replacePropertyDocumentFile.fulfilled, (state, action) => {
        const previousDocument = state.documentsByID[action.payload.document.documentID];
        syncDocumentBucketsForUpsert(state, previousDocument, action.payload.document);
        upsertDocument(state, action.payload.document);
        state.uploading = false;
      })
      .addCase(replacePropertyDocumentFile.rejected, (state, action) => {
        state.uploading = false;
        state.error = readError(action, 'Failed to replace property document file');
      })
      .addCase(fetchPropertyDocumentDownloadUrl.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchPropertyDocumentDownloadUrl.fulfilled, (state, action) => {
        state.activeDownloadUrl = action.payload.downloadUrl;
        state.loading = false;
      })
      .addCase(fetchPropertyDocumentDownloadUrl.rejected, (state, action) => {
        state.loading = false;
        state.error = readError(action, 'Failed to fetch document download URL');
      })
      .addCase(updatePropertyDocumentMetadata.fulfilled, (state, action) => {
        const previousDocument = state.documentsByID[action.payload.documentID];
        syncDocumentBucketsForUpsert(state, previousDocument, action.payload);
        upsertDocument(state, action.payload);
      })
      .addCase(updatePropertyDocumentMetadata.rejected, (state, action) => {
        state.error = readError(action, 'Failed to update property document');
      })
      .addCase(archivePropertyDocument.fulfilled, (state, action) => {
        upsertDocument(state, action.payload);
        removeDocumentFromBuckets(state, action.payload.documentID);
      })
      .addCase(archivePropertyDocument.rejected, (state, action) => {
        state.error = readError(action, 'Failed to archive property document');
      })
      .addCase(deletePropertyDocument.pending, (state, action) => {
        state.deletingByDocumentID[action.meta.arg.documentID] = true;
        state.error = undefined;
      })
      .addCase(deletePropertyDocument.fulfilled, (state, action) => {
        const documentID = action.payload.documentID;
        delete state.documentsByID[documentID];
        delete state.detailsByDocumentID[documentID];
        delete state.deletingByDocumentID[documentID];
        state.deletedByDocumentID[documentID] = true;
        removeDocumentFromBuckets(state, documentID);
      })
      .addCase(deletePropertyDocument.rejected, (state, action) => {
        state.deletingByDocumentID[action.meta.arg.documentID] = false;
        state.error = readError(action, 'Failed to delete property document');
      })
      .addCase(logoutUser.fulfilled, () => initialState);
  },
});

export default propertyDocumentsSlice.reducer;

export const { clearPropertyDocumentsError, clearPropertyDocumentDownloadUrl } = propertyDocumentsSlice.actions;

const selectPropertyDocumentsState = (state: RootState) => state.propertyDocuments;

export const selectPropertyDocumentTaxonomy = createSelector(
  [selectPropertyDocumentsState, (_: RootState, propertyID?: string | null) => propertyID],
  (state, propertyID) => (propertyID ? state.taxonomyByPropertyID[propertyID] : undefined),
);

export const selectPropertyDocumentBucket = createSelector(
  [
    selectPropertyDocumentsState,
    (_: RootState, propertyID?: string | null) => propertyID,
    (_: RootState, __: string | null | undefined, filters?: ListDocumentsFilters) => filters,
  ],
  (state, propertyID, filters) => {
    if (!propertyID) return EMPTY_DOCUMENTS;
    const bucket = state.bucketsByKey[buildPropertyDocumentBucketKey({ propertyID, filters })];
    if (!bucket) return EMPTY_DOCUMENTS;
    return bucket.documentIDs
      .map((documentID) => state.documentsByID[documentID])
      .filter((document): document is PropertyDocument => Boolean(document));
  },
);

export const selectPropertyDocumentBucketState = createSelector(
  [
    selectPropertyDocumentsState,
    (_: RootState, propertyID?: string | null) => propertyID,
    (_: RootState, __: string | null | undefined, filters?: ListDocumentsFilters) => filters,
  ],
  (state, propertyID, filters) => {
    if (!propertyID) return undefined;
    return state.bucketsByKey[buildPropertyDocumentBucketKey({ propertyID, filters })];
  },
);

export const selectPropertyDocumentEntityBucket = createSelector(
  [
    selectPropertyDocumentsState,
    (_: RootState, entityType?: DocumentLinkedEntityType | null) => entityType,
    (_: RootState, __: DocumentLinkedEntityType | null | undefined, entityID?: string | null) => entityID,
  ],
  (state, entityType, entityID) => {
    if (!entityType || !entityID) return EMPTY_DOCUMENTS;
    const bucket = state.entityBucketsByKey[buildPropertyDocumentEntityBucketKey({ entityType, entityID })];
    if (!bucket) return EMPTY_DOCUMENTS;
    return bucket.documentIDs
      .map((documentID) => state.documentsByID[documentID])
      .filter((document): document is PropertyDocument => Boolean(document));
  },
);

export const selectPropertyDocumentByID = createSelector(
  [selectPropertyDocumentsState, (_: RootState, documentID?: string | null) => documentID],
  (state, documentID) => (documentID ? state.documentsByID[documentID] : undefined),
);

export const selectPropertyDocumentDetail = createSelector(
  [selectPropertyDocumentsState, (_: RootState, documentID?: string | null) => documentID],
  (state, documentID) => (documentID ? state.detailsByDocumentID[documentID] : undefined),
);

export const selectPropertyDocumentDeleting = createSelector(
  [selectPropertyDocumentsState, (_: RootState, documentID?: string | null) => documentID],
  (state, documentID) => (documentID ? Boolean(state.deletingByDocumentID[documentID]) : false),
);

export const selectPropertyDocumentsLoading = createSelector(selectPropertyDocumentsState, (state) => state.loading);
export const selectPropertyDocumentsUploading = createSelector(
  selectPropertyDocumentsState,
  (state) => state.uploading,
);
export const selectPropertyDocumentsError = createSelector(selectPropertyDocumentsState, (state) => state.error);
export const selectPropertyDocumentDownloadUrl = createSelector(
  selectPropertyDocumentsState,
  (state) => state.activeDownloadUrl,
);
